import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import type { ColDef, GridApi, SortModelItem } from 'ag-grid-community';

import DataTable from 'components/DataTable';
import DataTableRenderers from 'components/DataTable/DataTable.renderers';
import { ShimSortModel } from 'components/DataTable/agParamsShim';
import useInfiniteRows from 'components/DataTable/useInfiniteRows';
import buildGridOptions from 'components/Grid/buildGridOptions';
import { useAggregatedBonusRows } from 'components/PerformanceBoards/hooks/useAggregatedBonusDatasource';
import useEntityField from 'components/TargetManager/hooks/useEntityField';
import { RowHeightContext } from 'contextProviders/SplashScreenProviders/MiscProviders/RowHeightProvider';

import toDataTableSizing from 'components/DataTable/toDataTableSizing';

const EMPTY_TOTALS: DataTable.Row[] = [];

// The selected entity's row (SelectedEntityProvider), as AG's StyleOverrides
// highlights it
const TableWrapper = styled.div`
  height: 100%;
  width: 100%;

  && tbody tr.performance-board-row-selected,
  && tbody tr.performance-board-row-selected:hover {
    background-color: var(--color-bg-selected);
  }
`;

type BoardTableProps = {
  board: PerformanceBoardTypes.Board;
  colDefs: ColDef[];
  // AG's gridOptions the table reads (rowClassRules)
  gridOptions: { [key: string]: any };
  totalsRow: DataTable.Row[] | undefined;
  // The overlay slide-out is open: keep the columns' widths, as AG skips
  // its refit
  isRefitPaused: boolean;
  setGridApi: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
  onExportHandle: (handle: DataTable.ExportHandle | undefined) => void;
};

// The board's rows drawn by DataTable, sized by the board's column size
// strategy, with its api (selection, scrolling, the slide-out's readiness)
// handed to the board's providers as AG's grid hands over its own
const BoardTable = ({
  board,
  colDefs,
  gridOptions,
  totalsRow,
  isRefitPaused,
  setGridApi,
  onExportHandle,
  rows,
  totalCount,
  isLoading,
  onEndReached,
  onSortChanged,
}: BoardTableProps & {
  rows: DataTable.Row[];
  totalCount: number | undefined;
  isLoading: boolean;
  onEndReached?: () => void;
  onSortChanged: (sortModel: ShimSortModel) => void;
}) => {
  const { rowHeight } = useContext(RowHeightContext);
  const entityField = useEntityField(board.dataType);
  const defaultColDef = useMemo(
    () => buildGridOptions({ rowHeight }).defaultColDef,
    [rowHeight],
  );
  const { sizing, columnDefs, fittedColumnIds, skipHeaderWhenSizing } = useMemo(
    () => toDataTableSizing({ strategy: board.columnSizeStrategy, colDefs }),
    [board.columnSizeStrategy, colDefs],
  );
  // A record's id where it has one, else its entity's (an aggregated row is
  // one per entity), else its position
  const getRowId = useCallback(
    (data: DataTable.Row, index: number) => {
      if (data.id !== undefined && data.id !== null) {
        return `${data.id}`;
      }
      const entity = entityField ? data[entityField] : undefined;
      return entity !== undefined && entity !== null
        ? `${entity}`
        : `row-${index}`;
    },
    [entityField],
  );
  const onGridReady = useCallback(
    ({ api }: { api: GridApi }) => setGridApi(api),
    [setGridApi],
  );

  return (
    <TableWrapper>
      <DataTable
        testId="grid"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        components={DataTableRenderers}
        rows={rows}
        totalsRows={totalsRow ?? EMPTY_TOTALS}
        gridOptions={gridOptions}
        sizing={sizing}
        fittedColumnIds={fittedColumnIds}
        skipHeaderWhenSizing={skipHeaderWhenSizing}
        isRefitPaused={isRefitPaused}
        isManualSorting
        onSortChanged={onSortChanged}
        onEndReached={onEndReached}
        isLoading={isLoading}
        noDataToDisplay={!isLoading && rows.length === 0}
        onExportHandle={onExportHandle}
        totalRowCount={totalCount}
        getRowId={getRowId}
        onGridReady={onGridReady}
        hasBorders
        hasDarkHeader
      />
    </TableWrapper>
  );
};

// The sort the board starts with, as AG's initialSort on its colDefs
const useSortModel = (sortOverride: SimpleGridSort) => {
  const [sortModel, setSortModel] = useState<SortModelItem[]>(() =>
    sortOverride.field
      ? [{ colId: sortOverride.field, sort: sortOverride.direction }]
      : [],
  );
  const onSortChanged = useCallback(
    (next: ShimSortModel) =>
      setSortModel((current) => (_.isEqual(current, next) ? current : next)),
    [],
  );
  return { sortModel, onSortChanged };
};

// A board's records from search2, a page at a time (AG's useDatasource)
const SearchRowsTable = ({
  dataType,
  filters,
  sortOverride,
  ...props
}: BoardTableProps & {
  dataType: string;
  filters: FilterInput[];
  sortOverride: SimpleGridSort;
}) => {
  const { sortModel, onSortChanged } = useSortModel(sortOverride);
  const { colDefs } = props;
  const getColDef = useCallback(
    (colId: string) => colDefs.find((c) => (c.colId ?? c.field) === colId),
    [colDefs],
  );
  const { rows, totalCount, isLoadingFirstPage, fetchNextPage } =
    useInfiniteRows({
      dataType,
      includeFields: undefined,
      filterInput: filters,
      sortModel,
      getColDef,
    });

  return (
    <BoardTable
      {...props}
      rows={rows}
      totalCount={totalCount}
      isLoading={isLoadingFirstPage}
      onEndReached={fetchNextPage}
      onSortChanged={onSortChanged}
    />
  );
};

// A sign-off board's rows aggregated by driver over the period (AG's
// useAggregatedBonusDatasource), all at once, sorted by the first sort key
const AggregatedRowsTable = ({
  filters,
  sortOverride,
  ...props
}: BoardTableProps & {
  filters: FilterInput[];
  sortOverride: SimpleGridSort;
}) => {
  const { sortModel, onSortChanged } = useSortModel(sortOverride);
  const { rows, isLoading } = useAggregatedBonusRows({
    board: props.board,
    filters,
    sortModel,
  });

  return (
    <BoardTable
      {...props}
      rows={rows}
      totalCount={undefined}
      isLoading={isLoading}
      onSortChanged={onSortChanged}
    />
  );
};

// A performance board's grid drawn by DataTable, in place of AG's
// server-side grid
const PerformanceBoardDataTableView = ({
  isAggregated,
  dataType,
  filters,
  sortOverride,
  ...props
}: BoardTableProps & {
  isAggregated: boolean;
  dataType: string;
  filters: FilterInput[];
  sortOverride: SimpleGridSort;
}) =>
  isAggregated ? (
    <AggregatedRowsTable
      {...props}
      filters={filters}
      sortOverride={sortOverride}
    />
  ) : (
    <SearchRowsTable
      {...props}
      dataType={dataType}
      filters={filters}
      sortOverride={sortOverride}
    />
  );

export default PerformanceBoardDataTableView;
